import {deepMerge} from '@sail/utils';

import {captureMessage} from '@sentry/browser';

import {AppConfig, AppProfile} from '../types';

type ClientFetch = typeof fetch;

function authedOrAnonUrl(requiresAuth: boolean, url: string) {
  const apiMatcher = /^\/?api\/(anon\/)?(.*)$/;

  if (requiresAuth) {
    return url.replace(apiMatcher, '/api/$2');
  }

  return url.replace(apiMatcher, '/api/anon/$2');
}

export function authedOrAnonRequest(
  requiresAuth: boolean,
  input: RequestInfo | URL,
): RequestInfo | URL {
  if (typeof input === 'string') {
    return authedOrAnonUrl(requiresAuth, input);
  }

  if ('url' in input) {
    return {
      ...input,
      url: authedOrAnonUrl(requiresAuth, input.url),
    };
  }

  // this shouldn't ever happen, but if it ever did I'd want to know
  captureMessage(
    "ApiProvider expected an api endpoint url, but didn't find one",
    {level: 'warning'},
  );

  return input;
}

function urlWithReferrerLocale(url: string, locale?: string): string {
  if (!locale) {
    return url;
  }
  const parsedUrl = new URL(url, window.location.origin);
  parsedUrl.searchParams.set('referrerLocale', locale);
  if (url.startsWith('/')) {
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  }
  return parsedUrl.toString();
}

function requestWithReferrerLocale(
  input: RequestInfo | URL,
  locale?: string,
): RequestInfo | URL {
  if (typeof input === 'string') {
    return urlWithReferrerLocale(input, locale);
  }

  if ('url' in input) {
    return {
      ...input,
      url: urlWithReferrerLocale(input.url, locale),
    };
  }

  captureMessage(
    "wrapFetch expected an api endpoint url, but didn't find one",
    {level: 'warning'},
  );

  return input;
}

export function wrapFetch(
  fetch: ClientFetch,
  getConfig: () => AppConfig,
  getProfile: () => AppProfile,
  onAuthFailure?: () => void,
): ClientFetch {
  const wrappedFetch: ClientFetch = async (req, init?) => {
    const config = getConfig();
    const {locale} = config;
    const profile = getProfile();
    const {livemode, visitor} = profile;
    const requiresAuth = visitor.sessionType !== 'anonymous';

    const reqWithAnonRoutes = authedOrAnonRequest(requiresAuth, req);

    // stripe-livemode is used by manage
    const livemodeHeaders =
      livemode != null ? {'stripe-livemode': livemode ? 'true' : 'false'} : {};

    const localeHeaders = {
      'Accept-Language': [locale, ...navigator.languages],
    };

    const headers = {
      ...livemodeHeaders,
      ...localeHeaders,
    };

    // @ts-expect-error - Accept-Language value should be a string, not an array
    const mergedInit = deepMerge({headers}, init) as {
      headers: {
        'Accept-Language': string;
        'stripe-livemode'?: string;
      };
    };

    const reqWithReferrerLocale = requestWithReferrerLocale(
      reqWithAnonRoutes,
      locale,
    );
    const response = await fetch(reqWithReferrerLocale, mergedInit);
    if (response.status === 401 && requiresAuth && onAuthFailure) {
      onAuthFailure();
    }
    return response;
  };
  return wrappedFetch;
}
